import {
  FaUsers,
  FaClipboardCheck,
  FaChartLine,
  FaCheckCircle,
  FaTimesCircle,
  FaTrophy,
} from "react-icons/fa";

import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LineChart,
  Line,
} from "recharts";

import { useGetCenterDashboardQuery } from "../../slices/quizAttemptApiSlice";

const COLORS = ["#16a34a", "#dc2626"];

const CenterDashboardScreen = () => {
  const { data, isLoading, error } = useGetCenterDashboardQuery();

  if (isLoading) {
    return <div className="p-10 text-center">Loading dashboard...</div>;
  }

  if (error) {
    return <div className="p-10 text-red-600">Unable to load dashboard</div>;
  }

  const dashboard = data?.data || {};

  const summary = dashboard.summary || {};

  const charts = dashboard.charts || {};

  const resultDistribution = charts.resultDistribution || [];

  const percentageDistribution = charts.percentageDistribution || [];

  const monthlyAttempts =
    charts.monthlyAttempts?.map((item) => ({
      month: `${item._id.month}/${item._id.year}`,

      attempts: item.attempts,
    })) || [];

  const topStudents = charts.topStudents || [];

  return (
    <div
      className="
min-h-screen
bg-gray-100
p-6
"
    >
      <h1
        className="
text-3xl
font-bold
mb-8
text-gray-800
"
      >
        Center Dashboard
      </h1>

      {/* ================= KPI CARDS ================= */}

      <div
        className="
grid
grid-cols-1
md:grid-cols-3
xl:grid-cols-6
gap-5
"
      >
        <InfoCard
          icon={<FaUsers />}
          title="Students"
          value={summary.totalStudents || 0}
        />

        <InfoCard
          icon={<FaClipboardCheck />}
          title="Attempts"
          value={summary.totalAttempts || 0}
        />

        <InfoCard
          icon={<FaChartLine />}
          title="Average %"
          value={`${summary.averagePercentage || 0}%`}
        />

        <InfoCard
          icon={<FaCheckCircle />}
          title="Pass"
          value={`${summary.passPercentage || 0}%`}
        />

        <InfoCard
          icon={<FaTimesCircle />}
          title="Fail"
          value={`${summary.failPercentage || 0}%`}
        />

        <InfoCard
          icon={<FaTrophy />}
          title="Highest"
          value={`${summary.highestPercentage || 0}%`}
        />
      </div>

      {/* ================= CHARTS ================= */}

      <div
        className="
grid
lg:grid-cols-2
gap-6
mt-8
"
      >
        {/* PASS FAIL PIE */}

        <ChartCard title="Result Distribution">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={resultDistribution}
                dataKey="value"
                nameKey="name"
                outerRadius={100}
                label
              >
                {resultDistribution.map((entry, index) => (
                  <Cell key={index} fill={COLORS[index]} />
                ))}
              </Pie>

              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* PERCENTAGE BAR */}

        <ChartCard title="Performance Distribution">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={percentageDistribution}>
              <CartesianGrid strokeDasharray="3 3" />

              <XAxis dataKey="range" />

              <YAxis />

              <Tooltip />

              <Bar dataKey="students" fill="#2563eb" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* MONTHLY LINE CHART */}

      <div
        className="
mt-6
"
      >
        <ChartCard title="Monthly Attempts">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthlyAttempts}>
              <CartesianGrid strokeDasharray="3 3" />

              <XAxis dataKey="month" />

              <YAxis />

              <Tooltip />

              <Line
                type="monotone"
                dataKey="attempts"
                stroke="#16a34a"
                strokeWidth={3}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* ================= TOP STUDENTS ================= */}

      <div
        className="
mt-6
bg-white
rounded-2xl
shadow
p-6
"
      >
        <h2
          className="
text-xl
font-bold
mb-5
"
        >
          Top Performing Students
        </h2>

        <table
          className="
w-full
text-left
"
        >
          <thead>
            <tr
              className="
border-b
"
            >
              <th className="p-3">Student</th>

              <th className="p-3">Attempts</th>

              <th className="p-3">Average %</th>
            </tr>
          </thead>

          <tbody>
            {topStudents.map((item, index) => (
              <tr
                key={index}
                className="
border-b
"
              >
                <td className="p-3">{item.student?.name}</td>

                <td className="p-3">{item.attempts}</td>

                <td
                  className="
p-3
font-semibold
text-green-600
"
                >
                  {Number(item.averagePercentage).toFixed(2)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const InfoCard = ({ icon, title, value }) => (
  <div
    className="
bg-white
rounded-2xl
shadow
p-5
"
  >
    <div
      className="
text-blue-600
text-2xl
"
    >
      {icon}
    </div>

    <p
      className="
mt-3
text-gray-600
"
    >
      {title}
    </p>

    <p
      className="
text-2xl
font-bold
"
    >
      {value}
    </p>
  </div>
);

const ChartCard = ({ title, children }) => (
  <div
    className="
bg-white
rounded-2xl
shadow
p-6
"
  >
    <h2
      className="
text-xl
font-bold
mb-5
"
    >
      {title}
    </h2>

    {children}
  </div>
);

export default CenterDashboardScreen;
